"use client";

import React, { forwardRef, VideoHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { getYouTubeEmbedUrl, validateYouTubeUrl } from "@/utils/video";

interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /**
   * The video source URL. Can be a direct video file URL or a YouTube URL.
   */
  src: string;
  /**
   * Video type (e.g., "video/mp4", "video/webm", or "youtube")
   * If not specified, will auto-detect if it's a YouTube URL
   */
  type?: string;
  /**
   * Whether to show video controls
   * @default true
   */
  controls?: boolean;
  /**
   * Whether to autoplay the video
   * @default false
   */
  autoPlay?: boolean;
  /**
   * Whether to loop the video
   * @default false
   */
  loop?: boolean;
  /**
   * Whether to mute the video
   * @default false
   */
  muted?: boolean;
  /**
   * Poster image URL (thumbnail shown before video plays)
   */
  poster?: string;
  /**
   * Additional className for styling
   */
  className?: string;
  /**
   * Container className for wrapper div
   */
  containerClassName?: string;
  /**
   * Video title for accessibility
   */
  title?: string;
  /**
   * Whether to make the video responsive (16:9 aspect ratio)
   * @default false
   */
  responsive?: boolean;
  /**
   * Callback when video is loaded
   */
  onLoadedData?: () => void;
  /**
   * Callback when video ends
   */
  onEnded?: () => void;
  /**
   * Callback when video plays
   */
  onPlay?: () => void;
  /**
   * Callback when video pauses
   */
  onPause?: () => void;
}

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      src,
      type,
      controls = true,
      autoPlay = false,
      loop = false,
      muted = false,
      poster,
      className,
      containerClassName,
      title = "Video",
      responsive = false,
      onLoadedData,
      onEnded,
      onPlay,
      onPause,
      ...props
    },
    ref
  ) => {
    // Auto-detect if the source is a YouTube URL
    const isYouTube = type === "youtube" || validateYouTubeUrl(src);

    // For responsive videos, wrap in a container with aspect ratio
    const videoContent = isYouTube ? (
      <iframe
        src={getYouTubeEmbedUrl(src, autoPlay, muted)}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={cn(
          "w-full h-full border-none",
          responsive ? "absolute inset-0" : "",
          className
        )}
      />
    ) : (
      <video
        ref={ref}
        src={src}
        controls={controls}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        poster={poster}
        onLoadedData={onLoadedData}
        onEnded={onEnded}
        onPlay={onPlay}
        onPause={onPause}
        className={cn(
          "w-full h-full",
          responsive ? "absolute inset-0 object-cover" : "",
          className
        )}
        {...props}
      >
        {type && <source src={src} type={type} />}
        Your browser does not support the video tag.
      </video>
    );

    if (responsive) {
      return (
        <div
          className={cn(
            "relative w-full overflow-hidden",
            "aspect-video", // 16:9 aspect ratio
            containerClassName
          )}
        >
          {videoContent}
        </div>
      );
    }

    return videoContent;
  }
);

Video.displayName = "Video";

export default Video;
